import React, { useState, useEffect } from 'react';
import { useFlowNavigation, useFlow } from '../../../contexts/FlowContext';
import { useSession } from '../../../contexts/SessionContext';
import { useMessages } from '../../../contexts/ThemeContext';
import PageLayout from '../../../components/PageLayout/CarelonRx';
import CancelChangePasswordModal from '../../../components/modals/CancelChangePasswordModal';
import AccountLockedModal from '../../../components/modals/AccountLockedModal';
import SessionExpiryModal from '../../../components/modals/SessionExpiryModal';
import ChangePasswordService from '../../../services/auth/ChangePasswordService';
import ContactService from '../../../services/contact/ContactService';
import SupportService from '../../../services/contact/SupportService';
import SecureSessionService from '../../../services/session/SecureSessionService';
import RateLimiter from '../../../security/RateLimiter';
import { PasswordValidator } from '../../../utils/passwordValidation';
import BusinessErrorMessage from '../error/BusinessErrorMessage';
import { ORIGINAL_CLIENT_REDIRECT_URL } from '../../../config';
import { getLogoutRedirectUrl } from '../../../utils/nativeApp';
import '../../../styles/CarelonRx/changePassword.css';

const ChangePasswordPage: React.FC = () => {
  const { nextStep } = useFlowNavigation();
  const { getFlowData, setFlowData, resetFlow } = useFlow();
  const { sessionData } = useSession();
  const messages = useMessages();
  const isNativeApp = getFlowData('device') === 'native_app';

  const username = sessionData?.userId || getFlowData('webadUsername') || '';
  const sessionId = sessionData?.sessionId || '';
  const currentPassword = getFlowData('currentPassword') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPasswordTouched, setNewPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [isFirstCharacter, setIsFirstCharacter] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAccountLockedModal, setShowAccountLockedModal] = useState(false);
  const [showBusinessError, setShowBusinessError] = useState(false);
  const [apiError, setApiError] = useState('');
  useEffect(() => {
    const storedSession = SecureSessionService.getStoredSession();
    if (storedSession?.expiresAt) {
      window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt: storedSession.expiresAt } }));
    }
  }, []);

  const passwordValidation = PasswordValidator.validatePassword(newPassword, username);
  const passwordsMatch = PasswordValidator.doPasswordsMatch(newPassword, confirmPassword);
  const showPasswordValidations = hasStartedTyping;
  const showConfirmValidations = confirmPassword.length > 0 || confirmPasswordTouched;
  const getPasswordRulesToShow = () => {
    
    if (newPasswordTouched && newPassword.trim() === '') {
      return passwordValidation.rules;
    }

    if (!showPasswordValidations) return [];

    if (isFirstCharacter && newPassword.length === 1) {
      return passwordValidation.rules;
    }
    return passwordValidation.rules.filter(rule => !rule.isValid);
  };
  
  const passwordRulesToShow = getPasswordRulesToShow();
  const isFormValid = 
    passwordValidation.isValid && 
    confirmPassword.length > 0 && 
    passwordsMatch &&
    !isChanging;

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    setApiError('');
    if (!hasStartedTyping && value.length > 0) {
      setHasStartedTyping(true);
    }
    if (isFirstCharacter && value.length > 1) {
      setIsFirstCharacter(false);
    }
    if (value.length === 0) {
      setIsFirstCharacter(true);
      setHasStartedTyping(false);
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    setApiError('');
  };

  const handleNewPasswordBlur = () => {
    setNewPasswordTouched(true);
  };

  const handleConfirmPasswordBlur = () => {
    setConfirmPasswordTouched(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewPasswordTouched(true);
    setConfirmPasswordTouched(true);
    if (!isFormValid) {
      return;
    }

    setIsChanging(true);
    setApiError('');

    const userIdentifier = sessionData?.userId || 'anonymous';
    const rateLimitResult = RateLimiter.isAllowed(`change_password_${userIdentifier}`, 5, 300000);

    if (!rateLimitResult.allowed) {
      setApiError('Too many attempts. Please wait 5 minutes before trying again.');
      setIsChanging(false);
      return;
    }

    try {
      const result = await ChangePasswordService.changePassword(
        newPassword,
        sessionId
      );

      if (result.success) {
        setFlowData('passwordChanged', true);
        
        const authResponse = getFlowData('authResponse');
        const oktaStatus = authResponse?.oktaAccountStatus?.toUpperCase();
        
        if (oktaStatus === 'LOCKED_OUT') {
          setShowAccountLockedModal(true);
          setIsChanging(false);
          return;
        }
        
        if (username) {
          try {
            const contactDetails = await ContactService.getMemberRegistrationDetails(
              username,
              sessionId
            );
            
            setFlowData('contactDetails', contactDetails);
            setFlowData('contactApiError', false);
            
            try {
              const supportContact = await SupportService.getSupportContactDetails(
                sessionId
              );
              
              if (supportContact) {
                setFlowData('supportContact', supportContact);
              }
            } catch (error) {
              console.error('Failed to fetch support contact:', error);
              setFlowData('supportContact', null);
            }
          } catch (error: any) {
            const statusCode = error?.response?.status;
            
            if (statusCode === 404) {
              console.error('Contact Details API returned 404 - User/Contact Not Found. Showing business error.');
              setShowBusinessError(true);
              setIsChanging(false);
              return; 
            }
            
            if (statusCode === 500 || statusCode === 503 || statusCode === 504) {
              console.error(`Contact Details API returned ${statusCode} - Server Error`);
            } else {
              if (statusCode) {
                console.error(`Contact Details API returned ${statusCode}:`, error);
              } else {
                console.error('Contact Details API failed (no HTTP status code):', error);
              }
              
              try {
                const supportContact = await SupportService.getSupportContactDetails(
                  sessionId
                );
                
                if (supportContact) {
                  setFlowData('supportContact', supportContact);
                }
              } catch (supportError) {
                console.error('Failed to fetch support contact after memberdetails error:', supportError);
                setFlowData('supportContact', null);
              }
            }
            
            setFlowData('contactApiError', true);
          }
        }
        
        nextStep();
      } else {
        setApiError('Sorry, looks like we are having technical issues. Please give us time to fix the issue. Then, try again.');
        setIsChanging(false);
      }
    } catch (error: any) {
      setApiError('Sorry, looks like we are having technical issues. Please give us time to fix the issue. Then, try again.');
      setIsChanging(false);
    }
  };


  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = () => {
    resetFlow();
    SecureSessionService.clearSession();
    setShowCancelModal(false);
    setNewPassword('');
    setConfirmPassword('');
    setNewPasswordTouched(false);
    setConfirmPasswordTouched(false);
    const cancelUrl = isNativeApp
      ? getLogoutRedirectUrl()
      : ORIGINAL_CLIENT_REDIRECT_URL;
    window.location.replace(cancelUrl || '/');
  };

  const handleCancelDismiss = () => {
    setShowCancelModal(false);
  };

  const handleBackToLogin = () => {
    SecureSessionService.clearSession();
    const backUrl = isNativeApp
      ? getLogoutRedirectUrl()
      : ORIGINAL_CLIENT_REDIRECT_URL;
    window.location.replace(backUrl || '/');
  };

  return (
    <PageLayout
      bannerTitle="Create A New Password"
    >
      <div className="login-page-container">
        <div className="login-left-panel">
          {!isNativeApp && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleBackToLogin();
              }}
              className="back-link"
            >
              <img src="/assets/carelonRx/images/Keyboard Arrow Left.svg" alt="" />
              <span>Back to Log In</span>
            </a>
          )}
          
          <h1 className="mfa-title change-password-title">Hello!</h1>
          <p className="mfa-subtitle change-password-subtitle">
            Choose a password that will be easy for you to remember but hard for others to guess.
          </p>

          <form id="change-password-form" onSubmit={handleSubmit} className="change-password-form">
            {/* New Password Field */}
            <div className="password-input-group">
              <label htmlFor="newPassword" className={`change-password-label ${newPasswordTouched && !passwordValidation.isValid ? 'error' : ''}`}>
                New Password
              </label>
              <div className="password-input-wrapper">
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  onBlur={handleNewPasswordBlur}
                  placeholder="Enter new password"
                  disabled={isChanging}
                  className={`crx-input ${newPasswordTouched && !passwordValidation.isValid ? 'error' : ''}`}
                  autoComplete="new-password"
                  aria-invalid={newPasswordTouched && !passwordValidation.isValid}
                  aria-describedby="password-policy"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  tabIndex={-1}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  <img src={showNewPassword ? "/assets/carelonRx/images/Visibility Off.svg" : "/assets/carelonRx/images/Visibility.svg"} alt="" />
                </button>
              </div>

              {/* New Password Validation Errors Container */}
              {(newPasswordTouched && (newPassword.trim() === '' || passwordRulesToShow.length > 0)) && (
                <div className="new-password-errors-outer">
                  <div className="new-password-errors-inner">
                    {/* Inline Required Field Error */}
                    {newPasswordTouched && newPassword.trim() === '' && (
                      <div className="change-password-policy">
                        <div className="change-password-policy-item invalid">
                          <img
                            src="/assets/carelonRx/images/info_error.svg"
                            alt=""
                            className="error-icon"
                          />
                          <span>Required field.</span>
                        </div>
                      </div>
                    )}

                    {/* Password Policy - Show validation rules when touched or typing */}
                    {newPasswordTouched && passwordRulesToShow.length > 0 && (
                      <div id="password-policy" className="change-password-policy">
                        {passwordRulesToShow.map((rule) => (
                          <div
                            key={rule.id}
                            className="change-password-policy-item invalid"
                          >
                            <img
                              src="/assets/carelonRx/images/info_error.svg"
                              alt=""
                              className="error-icon"
                            />
                            <span>{rule.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="password-input-group">
              <label htmlFor="confirmPassword" className={confirmPasswordTouched && (!confirmPassword || !passwordsMatch) ? 'error' : ''}>
                Confirm New Password
              </label>
              <div className="password-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  onBlur={handleConfirmPasswordBlur}
                  placeholder="Re-enter new password"
                  disabled={isChanging}
                  className={`crx-input ${confirmPasswordTouched && (!confirmPassword || !passwordsMatch) ? 'error' : ''}`}
                  autoComplete="new-password"
                  aria-invalid={confirmPasswordTouched && (!confirmPassword || !passwordsMatch)}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <img src={showConfirmPassword ? "/assets/carelonRx/images/Visibility Off.svg" : "/assets/carelonRx/images/Visibility.svg"} alt="" />
                </button>
              </div>

              {/* Confirm Password Validation Errors Container */}
              {(confirmPasswordTouched && (confirmPassword.trim() === '' || !passwordsMatch)) && (
                <div className="confirm-password-errors-outer">
                  <div className="confirm-password-errors-inner">
                    {/* Inline Required Field Error for Confirm Password */}
                    {confirmPasswordTouched && confirmPassword.trim() === '' && (
                      <div className="change-password-policy">
                        <div className="change-password-policy-item invalid">
                          <img
                            src="/assets/carelonRx/images/info_error.svg"
                            alt=""
                            className="error-icon"
                          />
                          <span>Required field.</span>
                        </div>
                      </div>
                    )}

                    {/* Passwords Don't Match Error */}
                    {confirmPasswordTouched && !passwordsMatch && (
                      <div className="change-password-policy">
                        <div className="change-password-policy-item invalid">
                          <img
                            src="/assets/carelonRx/images/info_error.svg"
                            alt=""
                            className="error-icon"
                          />
                          <span>Passwords don't match.</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* API Error */}
            {apiError && (
              <div className="change-password-policy">
                <div className="change-password-policy-item invalid">
                  <img
                    src="/assets/carelonRx/images/info_error.svg"
                    alt=""
                    className="change-password-policy-icon"
                  />
                  <span>{apiError}</span>
                </div>
              </div>
            )}

            <BusinessErrorMessage isVisible={showBusinessError} />
          </form>

          {/* Action Buttons - Following MFA Pattern */}
          <div className="mfa-footer">
            <div className="mfa-footer-buttons">
              <button
                type="button"
                onClick={handleCancel}
                className="crx-button crx-button-secondary"
                disabled={isChanging}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="change-password-form"
                className="crx-button crx-button-primary"
                disabled={!isFormValid}
              >
                {isChanging ? 'Processing...' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <CancelChangePasswordModal
        show={showCancelModal}
        onDismiss={handleCancelDismiss}
        onConfirm={handleCancelConfirm}
      />

      <AccountLockedModal
        isOpen={showAccountLockedModal}
        onClose={() => setShowAccountLockedModal(false)}
      />
      <SessionExpiryModal />
    </PageLayout>
  );
};

export default ChangePasswordPage;
